import type { OccupancySlot } from '../../types/venueBooking';

/** A foglaltság típusainak felirata. */
export const SLOT_TYPE_LABELS: Record<string, string> = {
  Training: 'Edzés',
  Match: 'Mérkőzés',
  Meeting: 'Megbeszélés',
  Other: 'Egyéb',
  Foglalás: 'Foglalás',
};

/**
 * Típusonként egységes színek, ugyanazok, mint az eseménykártyákon (edzés kék,
 * meccs piros, megbeszélés lila, foglalás narancs).
 */
export const SLOT_STYLES: Record<string, { block: string; bar: string; dot: string; pill: string }> = {
  Training: { block: 'bg-blue-50 text-blue-900 ring-blue-200', bar: 'bg-blue-500', dot: 'bg-blue-500', pill: 'bg-blue-100 text-blue-800' },
  Match: { block: 'bg-red-50 text-red-900 ring-red-200', bar: 'bg-red-500', dot: 'bg-red-500', pill: 'bg-red-100 text-red-800' },
  Meeting: { block: 'bg-purple-50 text-purple-900 ring-purple-200', bar: 'bg-purple-500', dot: 'bg-purple-500', pill: 'bg-purple-100 text-purple-800' },
  Other: { block: 'bg-gray-50 text-gray-900 ring-gray-200', bar: 'bg-gray-400', dot: 'bg-gray-400', pill: 'bg-gray-100 text-gray-700' },
  Foglalás: { block: 'bg-orange-50 text-orange-900 ring-orange-200', bar: 'bg-orange-500', dot: 'bg-orange-500', pill: 'bg-orange-100 text-orange-800' },
};

export const slotStyle = (type: string) => SLOT_STYLES[type] ?? SLOT_STYLES.Other;

/** Egy esemény/foglalás egyszer, a foglalt részeivel (null = teljes pálya). */
export interface GroupedSlot {
  slot: OccupancySlot;
  sections: number[] | null;
}

/** A foglaltsági API pályarészenként külön sort ad - eseményenként egy elemre vonjuk össze. */
export const groupSlots = (slots: OccupancySlot[]): GroupedSlot[] => {
  const map = new Map<string, GroupedSlot>();
  for (const s of slots) {
    if (s.isCancelled) continue;
    const key = `${s.source}-${s.id}`;
    const existing = map.get(key);
    if (!existing) {
      map.set(key, { slot: s, sections: s.section === null ? null : [s.section] });
    } else if (existing.sections !== null) {
      if (s.section === null) existing.sections = null;
      else if (!existing.sections.includes(s.section)) existing.sections.push(s.section);
    }
  }
  return Array.from(map.values())
    .map((g) => ({ ...g, sections: g.sections ? [...g.sections].sort((a, b) => a - b) : null }))
    .sort((a, b) => new Date(a.slot.startTime).getTime() - new Date(b.slot.startTime).getTime());
};

export const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export const timeOf = (value: string | Date) =>
  new Date(value).toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' });

export const getMonday = (date: Date): Date => {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() - day + (day === 0 ? -6 : 1));
  d.setHours(0, 0, 0, 0);
  return d;
};

export const addDays = (date: Date, days: number) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

export const isoDate = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

/** datetime-local érték. */
export const localDateTime = (date: Date) => `${isoDate(date)}T${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;

/**
 * A megjelenített órák: legalább 8–20, de ha van korábbi vagy későbbi foglalás,
 * az is belefér. Így nincs üres 6 órás sáv a rács tetején.
 */
export const hourRange = (slots: OccupancySlot[]): [number, number] => {
  let start = 8;
  let end = 20;
  for (const s of slots) {
    if (s.isCancelled) continue;
    const a = new Date(s.startTime);
    const b = new Date(s.endTime);
    start = Math.min(start, a.getHours());
    end = Math.max(end, b.getMinutes() > 0 ? b.getHours() + 1 : b.getHours());
  }
  return [Math.max(0, start), Math.min(24, end)];
};

/** A nap foglaltságai, ami belelóg (a több napos is). */
export const slotsOnDay = (slots: OccupancySlot[], day: Date) => {
  const from = new Date(day);
  from.setHours(0, 0, 0, 0);
  const to = addDays(from, 1);
  return slots.filter((s) => new Date(s.startTime) < to && new Date(s.endTime) > from);
};

/**
 * Az egymásba lógó blokkok egymás mellé kerülnek: minden elem kap egy sávot
 * (lane) és azt, hogy az átfedő csoportjában hány sáv van összesen.
 */
export const layoutLanes = <T,>(items: T[], start: (t: T) => number, end: (t: T) => number) => {
  const sorted = [...items].sort((a, b) => start(a) - start(b) || end(b) - end(a));
  const result = new Map<T, { lane: number; lanes: number }>();
  let cluster: T[] = [];
  let laneEnds: number[] = [];
  let clusterEnd = -Infinity;

  const flush = () => {
    cluster.forEach((t) => result.set(t, { ...result.get(t)!, lanes: laneEnds.length }));
    cluster = [];
    laneEnds = [];
  };

  for (const item of sorted) {
    if (start(item) >= clusterEnd) flush();
    let lane = laneEnds.findIndex((e) => e <= start(item));
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(end(item));
    } else {
      laneEnds[lane] = end(item);
    }
    result.set(item, { lane, lanes: 1 });
    cluster.push(item);
    clusterEnd = Math.max(clusterEnd, end(item));
  }
  flush();
  return result;
};
